<template>
  <div>
    <div
      class="uk-flex uk-flex-middle uk-flex-between uk-background-secondary uk-border-rounded-top"
      id="basisFormModalId"
    >
      <div class="uk-flex" style="margin-left: 15px">
        <img :src="`/icons/white/icon-white-book.svg`" style="width: 24px" />
        <h3
          style="color: white"
          class="uk-text-bolder uk-padding-small uk-margin-remove long-header"
        >
          Discussion Setup
        </h3>
      </div>
      <div class="uk-flex uk-flex-middle">
        <LoaderButton
          @click="submitDiscussion"
          class="heading-action-button uk-flex uk-flex-center uk-flex-middle uk-button uk-button-primary uk-margin-right-small uk-padding-small"
          data-tid="create-discussion"
          :disabled="submitDisabled"
          :loaderStatus="submitDiscussionButtonStatus"
        >
          <img
            :src="`/icons/white/icon-white-Create-Discussions.svg`"
            :style="{
              width: '20px',
              opacity: `${submitDisabled ? '.5' : '1'}`,
            }"
          />
          <span
            class="uk-text-bold uk-margin-small-left uk-line-height-small"
            data-cy="Post-document"
            v-text="`CREATE YOUR DISCUSSION`"
          />
        </LoaderButton>
        <span
          uk-icon="icon: close;ratio:1.3"
          class="close-img"
          style="margin-right: 15px; color: white"
          @click="closeModal"
        ></span>
      </div>
    </div>

    <div style="margin: 30px">
      <StepForm
        :activeStep="currentStep"
        :stepsVisited="maxStepVisited"
        :step="1"
        :totalSteps="totalSteps"
        @stepChange="handleStep"
      >
        <template v-slot:header>
          <HeadingSecondary title="Flow" :small="true" />
        </template>
        <template v-slot:body>
          <div>
            <div v-for="(f, idx) in Object.keys(formFields2)" :key="f">
              <template v-if="formFields2[f].type === 'option-buttons'">
                <template v-if="isFieldVisible(f)">
                  <div
                    :key="`${formFields2[f].type}_${idx}`"
                    class="uk-flex uk-flex-right uk-width-1-1 uk-margin-bottom"
                  >
                    <FormOptionButtons
                      class="uk-width-3-4"
                      :optionsType="f"
                      :options="formFields2[f].options"
                      v-model="form[f]"
                    />
                  </div>
                </template>
              </template>
            </div>
            <form>
              <template v-for="(f, idx) in Object.keys(formFields)" :key="`${f}_${idx}`">
                <form-group
                  v-if="
                    isFieldVisible(f) &&
                    formFields[f].type === 'text' &&
                    formFields[f].name === 'Discussion Title'
                  "
                  class="uk-width-1-1"
                >
                  <div class="uk-flex uk-flex-middle">
                    <span class="uk-text-bolder uk-text-emphasis uk-width-1-4"
                      >{{ formFields[f].name }}:</span
                    >
                    <input
                      class="uk-width-3-4 uk-input"
                      :class="{
                        'border-red': error[f] !== '' && !form[f] && formFields[f].required,
                      }"
                      :spellcheck="formFields[f].name !== 'Author(s)'"
                      data-tid="discussion-title"
                      :type="formFields[f].type"
                      :placeholder="formFields[f].placeholder"
                      v-model="form[f]"
                    />
                  </div>
                  <div
                    v-if="error[f] !== ''"
                    style="color: red; font-size: var(--global-font-size); margin-left: 25%"
                  >
                    {{ error[f] }}
                  </div>
                  <hr style="margin: 10px 0px" />
                </form-group>
              </template>
            </form>
          </div>
        </template>
      </StepForm>

      <StepForm
        :activeStep="currentStep"
        :stepsVisited="maxStepVisited"
        :step="2"
        :totalSteps="totalSteps"
        @stepChange="handleStep"
      >
        <template v-slot:header>
          <HeadingSecondary title="Framing" :small="true" />
        </template>
        <template v-slot:body>
          <div v-for="(f, idx) in Object.keys(formFields)" :key="f">
            <template
              v-if="formFields[f].type === 'option-buttons' && !formFields[f].options[0].isCurator"
            >
              <template v-if="isFieldVisible(f)">
                <div
                  :key="`${formFields[f].type}_${idx}`"
                  class="uk-flex uk-flex-middle uk-width-1-1"
                >
                  <span class="uk-text-bolder uk-text-emphasis uk-width-1-4"
                    >{{ formFields[f].name }}:</span
                  >
                  <FormOptionButtons
                    class="uk-width-3-4"
                    :optionsType="f"
                    :options="formFields[f].options"
                    v-model="form[f]"
                  />
                </div>
                <hr :key="`hr_${idx}`" style="margin: 10px 0 20px 0" />
              </template>
            </template>
            <template v-else-if="formFields[f].type === 'documentType' && isFieldVisible(f)">
              <span
                v-if="error.file !== ''"
                style="color: red; font-size: var(--global-font-size); font-style: italic"
                :key="`${f.type}_${idx}_err`"
                >{{ error.file }}
              </span>
              <CustomImport
                ref="customImport"
                v-if="form.documentType === 'custom'"
                :citeDebate="false"
                :fileLoadingCite="fileLoading"
                :form_prop.sync="form"
                @handleDocumentType="handleDocumentType"
                :formConfig="formConfig"
                :key="`${f.type}_${idx}_drop`"
              />
            </template>
          </div>
        </template>
      </StepForm>

      <StepForm
        :activeStep="currentStep"
        :stepsVisited="maxStepVisited"
        :step="3"
        :totalSteps="totalSteps"
        @stepChange="handleStep"
      >
        <template v-slot:header>
          <HeadingSecondary :title="'Basis'" :small="true" />
        </template>
        <template v-slot:body>
          <form>
            <template v-for="(f, idx) in Object.keys(formFields)" :key="`${f}_${idx}`">
              <template v-if="formFields[f].type === 'dropdown' && isFieldVisible(f)">
                <div class="uk-flex uk-flex-between">
                  <div class="uk-text-bold uk-text-secondary">Custom Fields:</div>
                  <button type="button" class="btn-custom" @click="custonFieldHandle">
                    <img
                      :class="{ 'rotate-arrow': !open }"
                      src="/icons/black/icon-black-outline-node.svg"
                    />
                  </button>
                </div>
              </template>
              <template v-else-if="formFields[f].type === 'array' && isFieldVisible(f)">
                <div :key="`${f.type}_${idx}`" :class="{ customFieldToggle: open }">
                  <div
                    v-for="(custom, index) in form[f]"
                    :key="`${index}`"
                    class="uk-flex uk-margin-bottom"
                  >
                    <form-group class="uk-width-1-4">
                      <input
                        class="uk-width-4-5 uk-input"
                        type="text"
                        placeholder="Custom Label"
                        v-model="form[f][index]['name']"
                      />
                    </form-group>
                    <form-group class="uk-width-3-4">
                      <input
                        class="uk-width-1-1 uk-input"
                        type="text"
                        placeholder="Custom field text"
                        v-model="form[f][index]['value']"
                      />
                    </form-group>
                    <button
                      type="button"
                      class="btn-remove uk-button uk-button-default uk-border-circle uk-padding-remove uk-margin-left"
                      @click="removeCustomField(index)"
                    >
                      <i
                        uk-icon="icon: ban; ratio:1.1"
                        class="ban-icon color: #9F1D35"
                        uk-tooltip="Remove Custom Field"
                      ></i>
                    </button>
                  </div>
                  <template>
                    <button
                      class="uk-button uk-button-default uk-margin-bottom uk-border-rounded"
                      type="button"
                      @click="addCustomField"
                      :class="{ customFieldToggle: open }"
                    >
                      <i uk-icon="plus"></i>
                      add
                    </button>
                  </template>
                </div>
              </template>

              <form-group
                v-else-if="
                  isFieldVisible(f) &&
                  formFields[f].type !== 'documentType' &&
                  formFields[f].type !== 'option-buttons' &&
                  formFields[f].name !== 'Discussion Title'
                "
                :key="`${f.type}_${idx}`"
                class="uk-width-1-1"
              >
                <div class="uk-flex uk-flex-middle">
                  <span class="uk-text-bolder uk-text-emphasis uk-width-1-4"
                    >{{ formFields[f].name }}:</span
                  >
                  <input
                    class="uk-width-3-4 uk-input"
                    :class="{ 'border-red': error[f] !== '' && !form[f] && formFields[f].required }"
                    :spellcheck="formFields[f].name !== 'Author(s)'"
                    :data-tid="
                      f === 'articleTitle' ? 'content-title' : f === 'author' ? 'author-input' : f
                    "
                    :type="formFields[f].type"
                    :placeholder="formFields[f].placeholder"
                    v-model="form[f]"
                  />
                </div>
                <div
                  v-if="error[f] !== ''"
                  style="color: red; font-size: var(--global-font-size); margin-left: 25%"
                >
                  {{ error[f] }}
                </div>
                <hr style="margin: 10px 0px" />
              </form-group>
            </template>
          </form>
        </template>
      </StepForm>

      <StepFormButtons
        :activeStep="currentStep"
        :totalSteps="totalSteps"
        @stepChange="handleStep"
        :fieldValidation="fieldValidation"
        :saveButton="false"
        :finalButton="{ text: '' }"
        :finalProcessStatus="LoadingStatus.NONE"
        :disabelBtn="false"
        :finalSaveAndClose="false"
        :citationFramingModalStep="citationFramingModal && currentStep === 2"
      />
    </div>
  </div>
</template>

<script>
import DocumentType from '@/assets/js/enum/DocumentType';
import DiscourseRole from '@/assets/js/enum/DiscourseRole';
import LoadingStatus from '@/assets/js/enum/LoadingStatus';
import LoaderButton from '@/components/display/button/LoaderButton.vue';
import { getOutlineNodeForTransfer } from '@/assets/js/utils';
import { mapState, mapMutations, mapActions } from 'vuex';
import FormOptionButtons from '@/components/form/FormOptionButtons.vue';
import CustomImport from '@/components/form/CustomImport.vue';
import { url } from '@vuelidate/validators';
import { xhr } from '@/xhr/endpoints';
import { EventBus } from '@/assets/js/event-bus';
import StepForm from '@/components/general/StepForm';
import StepFormButtons from '@/components/general/StepFormButtons';
import HeadingSecondary from '@/components/general/HeadingSecondary';

const contentFormatOptions = {
  custom: [
    {
      value: 'document',
      label: 'Document',
      tooltip: 'Long-form drafting',
    },
    {
      value: 'dialoge',
      label: 'Dialog',
      tooltip: 'For future release',
      disabled: true,
    },
  ],
  document: [
    {
      value: 'document',
      label: 'Document',
      tooltip: 'Long-form drafting',
    },
    {
      value: 'outline',
      label: 'Outline',
      tooltip: 'Index of key arguments',
    },
  ],
};
const contentCritiqueFormatOptions = {
  counterpart: [
    {
      value: 'Host',
      label: 'Make a Critique',
      tooltip: 'Challenge a position and invite stakeholders to defend it',
      disabled: false,
    },
    {
      value: 'Opposition',
      label: 'Invite a Critique',
      tooltip: 'Assert your position and invite challengers to test it',
      disabled: false,
    },
  ],
  curate: [
    {
      value: 'Host',
      label: 'Make a Critique',
      tooltip: 'Not available for Curated Discussions',
      disabled: true,
      isCurator: true,
    },
    {
      value: 'Opposition',
      label: 'Invite a Critique',
      tooltip: 'Assert your position and invite challengers to test it',
      disabled: false,
      isCurator: true,
    },
  ],
};

export default {
  name: 'NewBasisForm',

  components: {
    LoaderButton,
    FormOptionButtons,
    CustomImport,
    StepForm,
    StepFormButtons,
    HeadingSecondary,
  },

  props: {
    submitDiscussionButtonStatus: {
      type: String,
      default: LoadingStatus.NONE,
    },

    formConfig: {
      type: Object,
      default: null,
    },
    citationFramingModal: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      form: {
        discussiontitle: '',
        initialStance: 'Host',
        articleTitle: '',
        discussionType: 'curate',
        author: '',
        publication: 'The OpenVetting Forum',
        date: new Date().toISOString().slice(0, 10),
        documentType: 'custom',
        contentFormat: 'document',
        customFields: [
          {
            name: '',
            value: '',
          },
        ],
        file: {},
      },
      error: {
        discussiontitle: '',
        author: '',
        date: '',
        file: '',
      },
      formFields2: {
        discussionType: {
          type: 'option-buttons',
          options: [
            {
              value: 'counterpart',
              label: 'Challenge a Rival',
              tooltip: '',
              action: value => {
                this.form.initialStance = 'Host';
                this.formFields.initialStance.options = JSON.parse(
                  JSON.stringify(contentCritiqueFormatOptions[value])
                );
              },
              disabled: false,
            },
            {
              value: 'curate',
              label: 'Lead a Discussion',
              tooltip: '',
              action: value => {
                this.form.initialStance = 'Opposition';
                this.formFields.initialStance.options = JSON.parse(
                  JSON.stringify(contentCritiqueFormatOptions[value])
                );
              },
              disabled: false,
            },
          ],
        },
      },
      formFields: {
        discussiontitle: {
          name: 'Discussion Title',
          type: 'text',
          placeholder: 'Give your discussion a memorable name - Required',
          required: true,
        },
        initialStance: {
          name: 'Initial Stance',
          type: 'option-buttons',
          options: JSON.parse(JSON.stringify(contentCritiqueFormatOptions.counterpart)),
        },
        documentType: {
          name: 'Content Source',
          type: 'option-buttons',
          options: [
            {
              value: 'custom',
              label: 'Import',
              action: value => {
                this.form.contentFormat = 'document';
                this.formFields.contentFormat.options = contentFormatOptions[value];
              },
            },
            {
              value: 'document',
              label: 'Compose',
              action: value => {
                this.formFields.contentFormat.options = contentFormatOptions[value];
              },
            },
          ],
        },
        contentFormat: {
          name: 'Content Format',
          type: 'option-buttons',
          options: contentFormatOptions.custom,
        },
        documentTypePanel: {
          type: 'documentType',
        },
        articleTitle: {
          name: 'Content Title',
          type: 'text',
          placeholder: 'Optional',
        },
        publication: {
          name: 'Publisher',
          type: 'text',
          placeholder: 'Optional',
        },
        author: {
          name: 'Author(s)',
          type: 'text',
          placeholder: 'Required',
          required: true,
        },
        date: {
          name: 'Date Published',
          type: 'date',
          required: true,
        },
        customTags: {
          name: 'Custom Fields',
          type: 'dropdown',
        },
        customFields: {
          type: 'array',
        },
      },
      submitDisabled: true,
      fileLoading: false,
      open: true,
      currentStep: 1,
      totalSteps: 3,
      maxStepVisited: 1,
      importDataLength: false,
    };
  },

  computed: {
    LoadingStatus() {
      return LoadingStatus;
    },

    ...mapState(['idToken', 'teamLeadAccess']),
    fieldValidation() {
      let abcd = this.currentStep;
      if (abcd === 1) {
        return false;
      } else if (this.form.documentType === 'document') {
        return false;
      } else if (this.importDataLength) {
        return false;
      } else {
        return true;
      }
    },
  },

  watch: {
    'form.date': function (value) {
      // if date is invalid the input component will remove the value,
      // thus an empty value implies an invalid date
      if (!value) {
        this.error.date = 'You must enter a valid date';
        this.submitDisabled = true;
      } else {
        this.error.date = '';
        this.isError();
      }
    },

    'form.file.name': function (value) {
      if (this.form.file.type === 'url' && !value && !url(value)) {
        this.error.file = 'You must use a valid web address';
        this.submitDisabled = true;
      } else if (
        this.form.file.type === 'file' &&
        value !== undefined &&
        !value.includes('.txt') &&
        !value.includes('.docx')
      ) {
        this.error.file = 'Warning: only .txt or .docx files may be imported at this time';
        this.submitDisabled = true;
      } else {
        this.error.file = '';
        this.isError();
      }
    },

    'form.documentType': function (value) {
      if (value !== 'custom') {
        this.handleDocumentType(value);
      }
      this.isError();
    },

    'form.discussiontitle': function (value) {
      if (!value?.trim()) {
        this.error.discussiontitle = 'You must enter a title';
        this.submitDisabled = true;
      } else {
        this.error.discussiontitle = '';
        this.isError();
      }
    },

    'form.author': function (value) {
      if (!value?.trim()) {
        this.error.author = 'You must enter a name';
        this.submitDisabled = true;
      } else {
        this.error.author = '';
        this.isError();
      }
    },
    'form.file.data': function (value) {
      if (value) {
        this.importDataLength = true;
      } else {
        this.importDataLength = false;
      }
      if (!value) {
        this.submitDisabled = true;
      } else {
        this.isError();
      }
    },

    teamLeadAccess: {
      handler() {
        if (this.$el) {
          this.applyPermissions();
        }
      },
      deep: true,
      immediate: false,
    },
  },
  async mounted() {
    if (this.citationFramingModal) {
      this.currentStep = 2;
      this.maxStepVisited = 2;
    }
    EventBus.on('citation.extractArticle', this.extractArticle);
    EventBus.on('form.file.data.changes', this.formFileDataChange);

    if (!this.teamLeadAccess || Object.keys(this.teamLeadAccess).length === 0) {
      await this.getTeamLeadAccess();
    }

    this.applyPermissions();
  },
  beforeUnmount() {
    EventBus.off('citation.extractArticle', this.extractArticle);
    EventBus.off('form.file.data.changes', this.formFileDataChange);
  },
  methods: {
    ...mapMutations(['setDocument', 'setDocumentAnalyze']),

    ...mapActions([
      'showToastAlert',
      'getTeamLeadAccess',
      'getExtractedArticleStatus',
      'getExtractedArticle',
    ]),

    applyPermissions() {
      if (this.teamLeadAccess && Object.keys(this.teamLeadAccess).length > 0) {
        if (!this.teamLeadAccess.allowMakeCritique) {
          this.formFields.initialStance.options[0].disabled = true;
          this.formFields.initialStance.options[0].tooltip = 'For future release';
          if (this.teamLeadAccess.allowInviteCritique) {
            this.form.initialStance = 'Opposition';
          }
        }
        if (!this.teamLeadAccess.allowInviteCritique) {
          this.formFields.initialStance.options[1].disabled = true;
          this.formFields.initialStance.options[1].tooltip = 'For future release';
        }
      }
    },
    formFileDataChange(val) {
      if (val?.length !== 0 && val?.length !== undefined && val?.length !== null) {
        this.importDataLength = true;
      } else {
        this.importDataLength = false;
      }
    },
    custonFieldHandle() {
      this.open = !this.open;
    },
    addCustomField() {
      this.form.customFields.splice(this.form.customFields.length, 0, {
        name: '',
        value: '',
      });
    },
    setBasisFormData(data) {
      this.form = data;
    },
    removeCustomField(idx) {
      this.form.customFields.splice(idx, 1);
    },
    async submitDiscussion() {
      let document = {
        discussion_type: this.form.discussionType,
        title: this.form.discussiontitle,
        articleTitle: this.form.articleTitle,
        publication: this.form.publication,
        author: this.form.author,
        initialStance: this.form.initialStance,
        discourseRole:
          this.form.initialStance === 'Host' ? DiscourseRole.AUTHOR : DiscourseRole.STAKEHOLDER,
        organizationId: null,
        publicationDate: this.form.date,
      };

      let type = '';
      if (this.form.contentFormat === 'outline') {
        type = 'outline';
      } else {
        if (this.form.file.type) {
          if (this.form.file.type === 'url' && this.form.file.type !== 'cite') {
            type = 'url';
          } else if (this.form.file.type === 'file') {
            type = 'file';
          } else if (this.form.file.type === 'text') {
            type = 'document';
          }
        } else {
          type = 'document';
        }
      }

      if (type === 'url') {
        document = {
          ...document,
          documentType: DocumentType.ARTICLE,
          sourceURL: this.form.file.name,
          articleText: this.form.file.data,
        };
      } else if (type === 'document' || type === 'file') {
        document = {
          ...document,
          documentType: DocumentType.TEXT,
          documentText: this.form.file.data,
        };
      } else if (type === 'outline') {
        const sourceToSave = [
          getOutlineNodeForTransfer({
            level: '1',
            nodeContent: '',
            nodeOrder: '1',
            nodeKey: 'Node_0',
            nodeType: 'Outline',
          }),
        ];
        document = {
          ...document,
          documentType: DocumentType.OUTLINE,
          outlineType: 'Human-Outline',
          outline: sourceToSave,
          citations: [],
        };
      }

      this.$emit('submit', document, this.form.customFields);
    },

    async extractArticle() {
      this.fileLoading = true;
      try {
        let data = { url: this.form.file.name };
        let extractedArticle = await xhr.getExtractedArticle(data);

        let extractArticleStatus = await xhr.getExtractedArticleStatus(
          extractedArticle.source_document_id
        );

        // Check if processing is completed
        while (
          extractArticleStatus.processing_status === 'processing' ||
          extractArticleStatus.processing_status === 'requested'
        ) {
          // If processing is not completed, wait for 2 seconds and check again
          await new Promise(resolve => setTimeout(resolve, 2000));
          extractArticleStatus = await xhr.getExtractedArticleStatus(
            extractedArticle.source_document_id
          );
        }

        // Once processing is completed, get the particular document
        if (extractArticleStatus.processing_status === 'completed') {
          extractedArticle = await xhr.getParticularDocument(extractedArticle.source_document_id);

          // Update form data
          this.form.file.data = extractedArticle.analysis.text_content;
          if (!this.form.articleTitle) {
            this.form.articleTitle = extractedArticle.analysis.title;
          }
          if (!this.form.author) {
            this.form.author = extractedArticle.analysis.author;
          }
          this.form.date = extractedArticle.analysis.publication_date;
        } else {
          throw new Error('Processing status is not completed');
        }
        if (!this.form.publication || this.form.publication == 'OpenVetting') {
          this.form.publication = extractedArticle.analysis.publisher;
        }
        this.form.date = extractedArticle.analysis.publication_date;
      } catch (err) {
        this.form.file = {};
        this.showToastAlert({
          title: 'Extracting Document',
          message: 'Error fetching extracted article',
        });
      } finally {
        this.fileLoading = false;
      }
    },
    reset() {
      Object.assign(this.$data, this.$options.data.call(this));
    },

    handleDocumentType(value) {
      this.form.file = {};
      this.form.articleTitle = '';
      this.form.date = new Date().toISOString().slice(0, 10);
      if (value !== 'custom' && this.form.file.name) {
        this.reset();
        this.form.documentType = value;
      }
    },

    isError() {
      let error = false;
      for (let key in this.error) {
        if (
          !this.form[key] ||
          (key === 'file' && this.form.documentType === 'custom' && !this.form.file.data)
        ) {
          error = true;
          break;
        }
      }
      this.submitDisabled = error;
    },

    isFieldVisible(field) {
      return this.formConfig ? this.formConfig[field]?.visible !== false : true;
    },

    closeModal() {
      this.$emit('close');
    },
    handleStep(step) {
      this.currentStep = step;
      if (this.currentStep > this.maxStepVisited) {
        this.maxStepVisited = this.currentStep;
      }
    },
  },
};
</script>

<style lang="sass" scoped>
.long-header
  font-size: 120%
.close-img
  margin-left: 15px
  cursor: pointer
.link
  text-decoration: underline
  color: #0066ccff
.border-red
  border-color: red
.rotate-arrow
  transform: rotate(180deg)

.btn-custom
  border: none
  height: 30px

.uk-input
  border: 2px solid #516d79

.customFieldToggle
  display: none

.ban-icon
  color: #9F1D35
.capitalize
  text-transform: lowercase !important
  &::first-letter
   text-transform: uppercase !important
</style>
